import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import useSEO from '../../hooks/useSEO';
import { blogIndex } from '../../data/blogIndex';
import './Blog.css';

const BASE_URL = 'https://www.buyselltaxi.com';
const API_URL  = process.env.REACT_APP_API_URL || '/api';

// blogIndex is the source of truth for which posts exist: it is generated from
// blogPosts.js, which BlogPost.js renders straight from the bundle, and never
// calls the API for a slug it knows. The database is only a partial mirror —
// posts committed since the last seed have a live page but no row — so building
// this index from the API alone silently orphaned 87 of 133 posts from our own
// blog index. Start from the bundle and let the API enrich what it knows about.
// Metadata only by design: article bodies live in blogPosts.js and must not be
// pulled in here (see scripts/generate-blog-index.js).
const byNewest = (a, b) => new Date(b.publishedAt || 0) - new Date(a.publishedAt || 0);

const Blog = () => {
  const [posts, setPosts] = useState(() => [...blogIndex].sort(byNewest));

  useSEO({
    title: 'Taxi & Commercial Vehicle Blog – Buying Guides, Tips & News',
    description: 'Expert guides on buying and selling taxis in India. CNG vs diesel, fleet management, vehicle reviews, financing, and city-specific taxi market guides.',
    keywords: 'taxi buying guide India, commercial vehicle tips, how to buy taxi India, taxi business guide, used taxi review India',
    url: `${BASE_URL}/blog`,
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: BASE_URL },
          { '@type': 'ListItem', position: 2, name: 'Blog', item: `${BASE_URL}/blog` },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Blog',
        name: 'Buy Sell Taxi Blog',
        url: `${BASE_URL}/blog`,
        description: 'Expert guides and tips for buying and selling taxis and commercial vehicles in India.',
        publisher: {
          '@type': 'Organization',
          name: 'Buy Sell Taxi',
          url: BASE_URL,
        },
      },
    ],
  });

  useEffect(() => {
    let cancelled = false;

    axios.get(`${API_URL}/blog`)
      .then(res => {
        if (cancelled) return;
        const fromApi = res.data.posts || [];
        if (!fromApi.length) return;
        const merged = new Map(blogIndex.map(p => [p.slug, p]));
        fromApi.forEach(p => merged.set(p.slug, { ...merged.get(p.slug), ...p }));
        setPosts([...merged.values()].sort(byNewest));
      })
      .catch(err => {
        // Every post is already on the page from the bundle, so a failed fetch
        // costs us fresher metadata, not links. Nothing to show the reader.
        console.error('[Blog] fetch error:', err.message);
      });

    return () => { cancelled = true; };
  }, []);

  const categories = [...new Set(posts.map(p => p.category).filter(Boolean))];

  return (
    <div className="blog-page">
      <div className="blog-hero">
        <h1>Taxi &amp; Commercial Vehicle Blog</h1>
        <p>Buying guides, vehicle reviews, business tips, and market insights for India's taxi industry</p>
      </div>

      <div className="blog-container">
        <div className="blog-grid">
          {posts.map((post, i) => (
            <Link key={post.slug} to={`/blog/${post.slug}`} className={`blog-card${i === 0 ? ' blog-card-featured' : ''}`}>
              <div className="blog-card-body">
                <span className="blog-category">{post.category}</span>
                <h2 className="blog-card-title">{post.title}</h2>
                <p className="blog-card-excerpt">{post.excerpt}</p>
                <div className="blog-card-meta">
                  <span>{post.author}</span>
                  <span className="meta-dot">·</span>
                  <span>{post.publishedAt ? new Date(post.publishedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}</span>
                  <span className="meta-dot">·</span>
                  <span>{String(post.readTime || "").replace(/\s*read$/i, "")} read</span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div className="blog-sidebar">
          <div className="sidebar-widget">
            <h3>Categories</h3>
            <ul className="category-list">
              {categories.map(cat => (
                <li key={cat}>
                  <span>{cat}</span>
                  <span className="cat-count">{posts.filter(p => p.category === cat).length}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="sidebar-widget sidebar-cta">
            <h3>Ready to Buy or Sell?</h3>
            <p>Browse verified taxis across India or list your vehicle for free.</p>
            <Link to="/vehicles" className="sidebar-btn">Browse Vehicles</Link>
            <Link to="/seller/register" className="sidebar-btn sidebar-btn-secondary">Sell Your Taxi</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Blog;
